import { sampleWeather, sampleWorldCalendar, sampleSolar, type WorldEnvironmentState } from "@endclock/shared/environment";
import { BIOME_ATMOSPHERE_PROFILES, type AtmosphereFogProfile, type BiomeAtmosphereProfile } from "./atmosphere.js";
import { CAMERA_CONFIG } from "./camera.js";

type Color = readonly [number, number, number];
const clamp = (x: number): number => Math.max(0, Math.min(1, x));
const smooth = (a: number, b: number, x: number): number => { const t = clamp((x - a) / (b - a)); return t * t * (3 - 2 * t); };
const mix = (a: Color, b: Color, t: number): Color => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** +X east, -X west, -Z north, +Z south, +Y up. Direction points TOWARD the sun.
 * Calendar geometry is shared with climate and automatic handheld lighting. */
export function solarLighting(worldTimeMinutes: number, dayOfYear = 81) {
  const time = Number.isFinite(worldTimeMinutes) ? ((worldTimeMinutes % 1440) + 1440) % 1440 : 720;
  const solar = sampleSolar(dayOfYear, time);
  const direction: Color = [solar.sunDirection.x, solar.sunDirection.y, solar.sunDirection.z];
  const elevation = direction[1];
  // Exactly zero below the horizon; near-horizon light and shadows fade together.
  const intensity = smooth(0, 0.12, elevation) * (0.3 + 0.7 * smooth(0, 0.8, elevation));
  const color = mix([1, 0.36, 0.12], [1, 0.96, 0.86], smooth(0.02, 0.5, elevation));
  return { direction, elevation, intensity, color, shadowStrength: smooth(0.005, 0.10, elevation) };
}

export interface EnvironmentPresentation extends AtmosphereFogProfile {
  readonly fogDensity: number;
  readonly sunDirection: Color;
  /** Linear direct irradiance, already multiplied by intensity/cloud transmission. */
  readonly sunColor: Color;
  readonly ambientColor: Color;
  readonly skyZenithColor: Color;
  readonly sunShadowStrength: number;
  readonly cloudCoverage: number;
  readonly windDirectionRadians: number;
  readonly windSpeed: number;
  readonly snowCover: number;
  readonly groundWetness: number;
  readonly lightningFlash: number;
  readonly timeSeconds: number;
}

/** Legacy's orthographic eye is 112 m from the focus, so TPS's 108 m radial
 * fade would hide the ground. Retain the biome's projected-depth range in
 * clear weather, then bring intentional fog across the focus as it thickens. */
export function legacyEnvironmentFogProfile(environment: EnvironmentPresentation, biome: BiomeAtmosphereProfile): AtmosphereFogProfile {
  const amount = clamp(environment.fogDensity / 0.028);
  return {
    fogColor: environment.fogColor,
    fogNear: biome.fogNear + (CAMERA_CONFIG.distance - 28 - biome.fogNear) * amount,
    fogFar: biome.fogFar + (CAMERA_CONFIG.distance + 12 - biome.fogFar) * amount,
    fogMaximum: biome.fogMaximum + (1 - biome.fogMaximum) * amount,
  };
}

/** Preserve the biome's relative atmospheric tint without reintroducing its
 * former permanent dense fog or adding daylight-colored ambient at night. */
export function composeBiomeEnvironment(environment: EnvironmentPresentation, biome: BiomeAtmosphereProfile): EnvironmentPresentation {
  const baseline = BIOME_ATMOSPHERE_PROFILES.ASHLANDS;
  const tint = (color: Color, local: Color, reference: Color, weight: number): Color => color.map((value, index) => {
    const ratio = Math.max(0.7, Math.min(1.3, local[index]! / reference[index]!));
    return value * (1 + (ratio - 1) * weight);
  }) as unknown as Color;
  return { ...environment,
    ambientColor: tint(environment.ambientColor, biome.ambientColor, baseline.ambientColor, 0.5),
    fogColor: tint(environment.fogColor, biome.fogColor, baseline.fogColor, 0.35),
    skyZenithColor: tint(environment.skyZenithColor, biome.fogColor, baseline.fogColor, 0.18),
  };
}

export function buildEnvironmentPresentation(state: WorldEnvironmentState, serverEpoch: number, lightningFlash = 0): EnvironmentPresentation {
  const weather = sampleWeather(state, serverEpoch);
  const calendar = sampleWorldCalendar(state, serverEpoch);
  const sun = solarLighting(calendar.worldTimeMinutes, calendar.dayOfYear);
  const daylight = smooth(-0.20, 0.28, sun.elevation);
  const golden = smooth(-0.08, 0.06, sun.elevation) * (1 - smooth(0.12, 0.42, sun.elevation));
  const clouds = weather.cloudCoverage;
  const flash = clamp(lightningFlash);
  const ambient = mix([0.044, 0.061, 0.105], [0.55, 0.61, 0.69], daylight);
  const horizon = mix([0.017, 0.026, 0.055], [0.53, 0.65, 0.73], daylight);
  const warmHorizon = mix(horizon, [0.62, 0.30, 0.16], golden * 0.60 * (1 - clouds));
  const stormHorizon = mix(warmHorizon, mix([0.022, 0.029, 0.044], [0.27, 0.31, 0.35], daylight), weather.cloudDarkness * 0.80);
  const zenith = mix(mix([0.005, 0.010, 0.030], [0.15, 0.32, 0.51], daylight), mix([0.01, 0.015, 0.028], [0.21, 0.26, 0.31], daylight), weather.cloudDarkness * 0.9);
  const transmission = weather.sunAttenuation;
  const fogAmount = clamp(weather.fogDensity / 0.028);
  return {
    fogDensity: weather.fogDensity,
    sunDirection: sun.direction,
    sunColor: sun.color.map((x) => x * sun.intensity * transmission * 1.13) as unknown as Color,
    ambientColor: ambient.map((x) => x * weather.ambientMultiplier + flash * 0.75) as unknown as Color,
    skyZenithColor: mix(zenith, [0.68, 0.74, 0.88], flash * 0.85),
    fogColor: mix(stormHorizon, [0.73, 0.79, 0.9], flash * 0.75),
    // Clear visibility extends to 80 m; complete ray-matched edge fade still
    // finishes within the 128 m terrain radius minus camera boom/follow margin.
    fogNear: 80 - fogAmount * 73,
    fogFar: 108 - fogAmount * 72,
    fogMaximum: 1,
    sunShadowStrength: sun.shadowStrength,
    cloudCoverage: clouds,
    windDirectionRadians: weather.windDirectionRadians,
    windSpeed: weather.windSpeed,
    snowCover: state.snowCover,
    groundWetness: state.groundWetness,
    lightningFlash: flash,
    timeSeconds: serverEpoch / 1000,
  };
}
